// cabang.view — daftar Cabang + form + gembok + hapus. Port renderCabang/outletForm v0.18.
import { esc } from '@domain/format';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { surveiCabangUrl } from '@domain/publicLink';
import { alamatPublik } from '@modules/documents/documents.doc';
import type { Outlet } from '@core/types';
import * as repo from './cabang.repo';
import * as svc from './cabang.service';

export async function renderCabang(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="ttl">🏢 Cabang</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let list: Outlet[], emps: repo.EmpBrief[];
  try { [list, emps] = await Promise.all([repo.listCabang(), repo.listEmployeesBrief()]); }
  catch (e) { root.innerHTML = `<div class="ttl">🏢 Cabang</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const empCount = (kode: string): number => emps.filter((k) => k.outlet === kode).length;
  const manage = svc.canManageCabang();
  const bt = 'class="btn" style="padding:4px 9px"';
  const body = list.map((o) => {
    const gembok = o.gembok
      ? `<span class="pill" style="background:var(--dangerbg);color:var(--danger)">🔒 Dikunci</span>`
      : `<span class="pill" style="background:var(--okbg);color:var(--ok)">🔓 Buka</span>`;
    const n = empCount(o.kode);
    const k = esc(o.kode); // escape utk konteks atribut data-* (defense-in-depth)
    const staffing = `<button ${bt} data-emp="${k}" title="Lihat karyawan cabang ini">👥 ${n}</button>`;
    const act = [
      `<button ${bt} data-svlink="${k}" title="Link survei cabang ini">🔗</button>`, // semua peran boleh menyalin link publik
      ...(manage ? [
        `<button ${bt} data-lock="${k}" title="${o.gembok ? 'Buka toko' : 'Kunci toko'}">${o.gembok ? '🔓' : '🔒'}</button>`,
        `<button ${bt} data-edit="${k}" title="Ubah">✏️</button>`,
        `<button ${bt} data-del="${k}" title="Hapus cabang">🗑️</button>`,
      ] : []),
    ].join(' ');
    return `<tr><td><b>${esc(o.kode)}</b></td><td>${esc(o.nama)}</td><td>${esc(o.kota ?? '—')}</td><td>${esc(o.telp ?? '—')}</td><td>${esc(o.wa ?? '—')}</td><td>${staffing}</td><td>${gembok}</td><td style="white-space:nowrap">${act}</td></tr>`;
  }).join('') || `<tr><td colspan="8" class="empty">Belum ada cabang.</td></tr>`;

  root.innerHTML = `<div class="ttl">🏢 Cabang <span class="pill" style="background:var(--ice);color:var(--navy)">${list.length}</span></div>
    <div class="scope-note">Master cabang (multi-outlet). Kontak & rekening tampil di dokumen. Gembok = kunci input data servis per cabang. Kelola anggota di Pengaturan → Absensi.</div>
    <div class="toolbar" style="display:flex;gap:8px;margin:10px 0;flex-wrap:wrap">
      ${manage ? `<button class="btn primary" id="c_add">➕ Cabang Baru</button>` : ''}
      <button class="btn" id="c_svpusat" title="Link survei gabungan semua cabang">🔗 Link Survei Pusat</button></div>
    <table class="data"><thead><tr><th>Kode</th><th>Nama</th><th>Kota</th><th>Telp</th><th>WA</th><th>Karyawan</th><th>Toko</th><th>Aksi</th></tr></thead><tbody>${body}</tbody></table>`;

  const refresh = (): void => { void renderCabang(root); };
  root.querySelector('#c_add')?.addEventListener('click', () => cabangForm(null, list, refresh));
  root.querySelector('#c_svpusat')?.addEventListener('click', () => surveiLinkDialog('ALL', 'Pusat (gabungan semua cabang)'));
  root.querySelectorAll<HTMLElement>('button[data-svlink]').forEach((b) =>
    b.addEventListener('click', () => { const o = list.find((x) => x.kode === b.dataset.svlink); if (o) surveiLinkDialog(o.kode, o.nama); }));
  root.querySelectorAll<HTMLElement>('button[data-emp]').forEach((b) =>
    b.addEventListener('click', () => { const o = list.find((x) => x.kode === b.dataset.emp); if (o) cabangMembersDialog(o, emps.filter((k) => k.outlet === o.kode)); }));
  root.querySelectorAll<HTMLElement>('button[data-edit]').forEach((b) =>
    b.addEventListener('click', () => { const o = list.find((x) => x.kode === b.dataset.edit); if (o) cabangForm(o, list, refresh); }));
  root.querySelectorAll<HTMLElement>('button[data-lock]').forEach((b) =>
    b.addEventListener('click', async () => {
      const o = list.find((x) => x.kode === b.dataset.lock); if (!o) return;
      try { await svc.toggleGembok(o.kode, !o.gembok); toast(o.gembok ? '🔓 Toko dibuka' : '🔒 Toko dikunci'); refresh(); } catch (e) { toast((e as Error).message); }
    }));
  root.querySelectorAll<HTMLElement>('button[data-del]').forEach((b) =>
    b.addEventListener('click', async () => {
      const o = list.find((x) => x.kode === b.dataset.del); if (!o) return;
      if (!confirm(`Hapus cabang ${o.kode} — ${o.nama}?`)) return;
      try { await svc.deleteCabang(o.kode, list.length); toast('🗑️ Cabang dihapus'); refresh(); } catch (e) { toast((e as Error).message); }
    }));
}

/** Daftar karyawan cabang (read-only). Manajemen add/keluarkan anggota di Pengaturan → Absensi (faithful v18 Batch 47). */
function cabangMembersDialog(o: Outlet, members: repo.EmpBrief[]): void {
  const rows = members.length
    ? members.map((k) => `<tr><td><b>${esc(k.nama)}</b></td><td>${esc(k.role)}</td></tr>`).join('')
    : `<tr><td colspan="2" class="empty">Belum ada karyawan aktif di cabang ini.</td></tr>`;
  const bg = openModal(`<div class="modal-head"><h3>👥 Karyawan — ${esc(o.nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <table class="data"><thead><tr><th>Nama</th><th>Divisi</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="scope-note" style="margin-top:10px">Read-only. Tambah/keluarkan anggota & atur shift di <b>Pengaturan → Absensi</b>.</div>
    </div><div class="modal-foot"><button class="btn" id="cm_close">Tutup</button></div>`);
  bg.querySelector('#cm_close')?.addEventListener('click', () => bg.remove());
}

/** Link survei STANDING untuk satu cabang (atau 'ALL' = pusat). Pelanggan buka → masukkan No HP →
 *  nilai servis terakhirnya. Beda dari QR per-nota (#t=): satu link tetap, tempel di mana saja
 *  (struk, stiker meja, status WA). Tetap phone-gated & 1×/servis. */
function surveiLinkDialog(kode: string, nama: string): void {
  const url = surveiCabangUrl(alamatPublik(), kode);
  const pusat = kode === 'ALL';
  const bg = openModal(`<div class="modal-head"><h3>🔗 Link Survei — ${esc(nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">${pusat
        ? 'Link <b>pusat</b>: pelanggan cabang mana pun bisa memakainya. Penilaian tetap masuk ke cabang tiketnya masing-masing.'
        : 'Link tetap untuk cabang ini — tempel di struk, stiker meja, atau status WA. Penilaian masuk ke cabang ini.'}
        Pelanggan memasukkan No. HP dulu; hanya pemilik servis yang bisa menilai (sekali per servis).</div>
      <div class="field"><label>Tautan</label>
        <input id="sl_url" readonly value="${esc(url)}" style="width:100%;font:inherit" onclick="this.select()"></div>
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        <button class="btn primary" id="sl_copy">📋 Salin tautan</button>
        <a class="btn" href="${esc(url)}" target="_blank" rel="noopener" style="text-decoration:none">↗ Buka</a></div>
      <div class="scope-note" style="margin-top:8px">Tautan ikut alamat aplikasi saat ini${pusat ? '' : ` (kode <b>${esc(kode)}</b>)`}. Setel domain khusus di Pengaturan → Profil → “Alamat aplikasi (publik)”.</div>
    </div>
    <div class="modal-foot"><button class="btn" id="sl_close">Tutup</button></div>`);
  bg.querySelector('#sl_close')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#sl_copy')?.addEventListener('click', async () => {
    const inp = bg.querySelector('#sl_url') as HTMLInputElement;
    try { await navigator.clipboard.writeText(inp.value); toast('📋 Tautan disalin'); }
    catch { inp.select(); document.execCommand('copy'); toast('📋 Tautan disalin'); } // fallback konteks non-secure
  });
}

function cabangForm(o: Outlet | null, all: Outlet[], done: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>${o ? 'Ubah' : 'Tambah'} Cabang</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="field"><label>Kode Cabang <span class="req">*</span> <span style="color:var(--muted);font-weight:400">(mis. PKP)</span></label><input id="cf_kode" value="${esc(o?.kode ?? '')}" ${o ? 'disabled' : ''} style="text-transform:uppercase"></div>
      <div class="field"><label>Nama Cabang <span class="req">*</span></label><input id="cf_nama" value="${esc(o?.nama ?? '')}"></div>
      <div class="field"><label>Kota</label><input id="cf_kota" value="${esc(o?.kota ?? '')}"></div>
      <div class="row2"><div class="field"><label>No. Telp cabang</label><input id="cf_telp" value="${esc(o?.telp ?? '')}"></div>
        <div class="field"><label>No. WA cabang</label><input id="cf_wa" value="${esc(o?.wa ?? '')}" placeholder="62812…"></div></div>
      <div class="field"><label>Alamat cabang</label><input id="cf_alamat" value="${esc(o?.alamat ?? '')}"></div>
      <div class="row2"><div class="field"><label>Rekening cabang</label><input id="cf_rek" value="${esc(o?.rekening ?? '')}" placeholder="Bank · No · a/n"></div>
        <div class="field"><label>Link Google Maps (review)</label><input id="cf_gmaps" value="${esc(o?.gmaps ?? '')}" placeholder="https://maps.app.goo.gl/…"></div></div>
      <div class="err" id="cf_err"></div>
    </div>
    <div class="modal-foot"><button class="btn" id="cf_cancel">Batal</button><button class="btn primary" id="cf_save">Simpan</button></div>`);

  const val = (id: string): string => (bg.querySelector('#' + id) as HTMLInputElement | null)?.value ?? '';
  bg.querySelector('#cf_cancel')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#cf_save')?.addEventListener('click', async () => {
    const form: svc.CabangForm = { kode: val('cf_kode'), nama: val('cf_nama'), kota: val('cf_kota'), telp: val('cf_telp'), wa: val('cf_wa'), alamat: val('cf_alamat'), rekening: val('cf_rek'), gmaps: val('cf_gmaps') };
    try { await svc.saveCabang(o?.kode ?? null, form, all); bg.remove(); toast('✅ Tersimpan'); done(); }
    catch (e) { const el = bg.querySelector('#cf_err'); if (el) el.textContent = (e as Error).message; }
  });
}
